import * as React from 'react';

import { ActivityPacing } from '@showbie-socrative/socrative-utils';
import { useAtom } from 'jotai';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { translate } from '~l10n/client';
import { languageAtom } from '~shared/atoms';
import { Divider } from '~shared/components/Divider';
import { IconInformation } from '~shared/components/icons/micro';
import { IconCheckmark, IconX } from '~shared/components/icons/standard';
import { Popover } from '~shared/components/Popover/Popover';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { ActivitySettingsCamelCase } from '~teacher/models/activities';

export function ResultsSettings({
  settings,
  startDate,
  endDate,
  libraryName,
}: {
  settings: ActivitySettingsCamelCase;
  startDate: string;
  endDate: string;
  /** Source shared library; omitted for quizzes not launched from a library. */
  libraryName?: string;
}) {
  const [language] = useAtom(languageAtom);
  const appContext = useTeacherApplicationContext();

  let deliveryMethod = translate('Instant Feedback');
  if (settings.pacing === ActivityPacing.Teacher) {
    deliveryMethod = translate('Teacher Paced');
  }
  if (settings.allowStudentNav) {
    deliveryMethod = translate('Open Navigation');
  }

  return (
    <Popover
      mode={'click'}
      disableArrow={true}
      content={
        <div
          className={'text-sm font-400 leading-8 text-blue-900'}
          data-testid={'results-setting-popover'}
        >
          <h3
            className={
              'text-xs font-700 leading-8 text-grey-800 tracking-wider uppercase pb-2 m-0'
            }
          >
            {translate('Library')}
          </h3>

          <div className={'flex flex-center'}>
            {/* Falls back to the personal library when not launched from a shared one. */}
            <span>{libraryName ?? translate('Personal')}</span>
          </div>

          <div className="pt-4">
            <Divider />
          </div>

          <h3
            className={
              'text-xs font-700 leading-8 text-grey-800 tracking-wider uppercase pt-4 pb-2 m-0'
            }
          >
            {translate('Delivery Method')}
          </h3>

          <div className={'flex flex-center'}>
            <span>{translate('{0}').format(deliveryMethod)}</span>
          </div>

          <div className="pt-4">
            <Divider />
          </div>

          <h3
            className={
              'text-xs font-700 leading-8 text-grey-800 tracking-wider uppercase pt-4 pb-2 m-0'
            }
          >
            {translate('Settings')}
          </h3>

          <ul role={'list'}>
            {appContext === TeacherApplicationContext.Socrative &&
              settings.requireNames !== undefined && (
                <li className={'flex flex-center'} role={'listitem'}>
                  <CheckOrCross
                    value={settings.requireNames}
                    titleId={'require-names'}
                  />
                  <span>{translate('Require Names')}</span>
                </li>
              )}

            {settings.randomQuestions !== undefined && (
              <li className={'flex flex-center'}>
                <CheckOrCross
                  value={settings.randomQuestions}
                  titleId={'shuffle-questions'}
                />
                <span>{translate('Shuffle Questions')}</span>
              </li>
            )}

            {settings.randomAnswers !== undefined && (
              <li className={'flex flex-center'}>
                <CheckOrCross
                  value={settings.randomAnswers}
                  titleId={'shuffle-answers'}
                />
                <span>{translate('Shuffle Answers')}</span>
              </li>
            )}

            {settings.showFeedback !== undefined && (
              <li className={'flex flex-center'}>
                <CheckOrCross
                  value={settings.showFeedback}
                  titleId={'show-question-feedback'}
                />
                <span>{translate('Show Question Feedback')}</span>
              </li>
            )}

            {settings.showResults !== undefined && (
              <li className={'flex flex-center'}>
                <CheckOrCross
                  value={settings.showResults}
                  titleId={'show-final-score'}
                />
                <span>{translate('Show Final Score')}</span>
              </li>
            )}
            {settings.allowRepeatResponses !== undefined && (
              <li className={'flex flex-center'}>
                <CheckOrCross
                  value={settings.allowRepeatResponses}
                  titleId={'allow-unlimited-responses'}
                />
                <span>{translate('Allow unlimited responses')}</span>
              </li>
            )}
            {settings.oneAttempt !== undefined && (
              <li className={'flex flex-center'}>
                <CheckOrCross
                  value={settings.oneAttempt}
                  titleId={'one-attempt'}
                />
                <span>{translate('One Attempt')}</span>
              </li>
            )}
            {(settings.quizTimerSeconds ?? -1) > 0 ? (
              <li className={'flex flex-center'}>
                <CheckOrCross value={true} titleId={'quiz-timer'} />
                <span>
                  {/* TODO: In future, we will have Teacher starts option */}
                  {translate(
                    'Time limit: {{minutes}} min · Student starts'
                  ).replace(
                    '{{minutes}}',
                    String(Math.floor(settings.quizTimerSeconds / 60))
                  )}
                </span>
              </li>
            ) : (
              <li className={'flex flex-center'}>
                <CheckOrCross value={false} titleId={'quiz-timer'} />
                <span>{translate('Time Limit')}</span>
              </li>
            )}
          </ul>

          {startDate && endDate ? (
            <>
              <div className="pt-2">
                <Divider />
              </div>

              <h3
                className={
                  'text-xs font-700 leading-8 text-grey-800 tracking-wider uppercase m-0 pt-4 pb-2'
                }
              >
                {translate('Duration')}
              </h3>

              <ul role={'list'}>
                {startDate && (
                  <li role={'listitem'}>
                    <span>
                      {new Date(startDate).toLocaleDateString(language, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                      })}
                    </span>
                  </li>
                )}
                {endDate && (
                  <li role={'listitem'}>
                    <span>
                      {new Date(endDate).toLocaleDateString(language, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                      })}
                    </span>
                  </li>
                )}
              </ul>
            </>
          ) : (
            false
          )}
        </div>
      }
      direction={'bottom-start'}
      enablePersistence
    >
      <button
        className={
          'flex flex-center cursor-pointer bg-white border-none p-0 m-0 m-4 w-auto h-auto text-blue-800'
        }
        onClick={() => {
          new FeatureUseEventAdapter({
            feature: 'delivery_method_info',
            action: 'click',
          }).sendAnalytics();
        }}
      >
        <div
          className="w-8 h-8 rounded-round border-solid border-grey-200 rounded-full flex justify-center items-center"
          aria-label={translate('Additional Information')}
        >
          <IconInformation />
        </div>
      </button>
    </Popover>
  );
}

const CheckOrCross = ({
  value,
  titleId,
}: {
  value: boolean;
  titleId: string;
}) => {
  return (
    <div className={'pr-1'}>
      {value ? (
        <IconCheckmark
          title={translate('Checkmark')}
          titleId={`icon-title-${titleId}`}
        />
      ) : (
        <IconX
          className={'fill-grey-500'}
          title={'X'}
          titleId={`icon-title-${titleId}`}
        />
      )}
    </div>
  );
};
